/**
 * 수강권 순번 계산 — 브리핑 판단의 뿌리.
 *
 * ─────────────────────────────────────────────────────────────────
 * 왜 이 파일이 따로 있나
 * ─────────────────────────────────────────────────────────────────
 * 6차 검증에서 수강권 순번이 이탈을 지배하는 변수로 나왔다.
 *   1번째 68.3% · 2번째 48.2% · 3번째 34.4%  (스프레드 50.5%p)
 * 순번이 하나 틀리면 위험도 판정이 통째로 뒤집힌다. 그래서 계산을 한 곳에만 두고
 * 화면 여러 군데에서 각자 세지 않게 한다.
 *
 * ─────────────────────────────────────────────────────────────────
 * 핵심 함정 — 수강권이 하나여도 첫 수강권이 아닐 수 있다
 * ─────────────────────────────────────────────────────────────────
 * 라포에서는 수강권을 끊을 때 신규/재등록을 구분해서 등록한다
 * (서버: membership.isNewSales, 기본값 true).
 *
 * 라포 도입 전부터 다니던 회원이 도입 후 처음 끊은 수강권을 재등록으로 등록하면,
 * 라포에 남은 기록은 한 건뿐이다. 이걸 그냥 세면 "1번째 수강권"이 되지만
 * 실제로는 이미 센터를 다녀본 회원이라 이탈률이 68.3%인 집단이 아니다.
 *
 * 그래서 규칙은 이렇다.
 *   기록상 순번 + (가장 이른 수강권이 재등록으로 분류됐으면 +1)
 *
 * 다만 이전에 몇 번 끊었는지는 라포가 알 수 없다. 한 번일 수도, 다섯 번일 수도 있다.
 * 그래서 결과를 "2번째"가 아니라 "2번째 이상"으로 표시하고 isAtLeast 를 세운다.
 * 모르는 것을 아는 척하면 안 된다.
 *
 * ─────────────────────────────────────────────────────────────────
 * 서버 원본 (rappo-server)
 * ─────────────────────────────────────────────────────────────────
 * - membership.isNewSales: Boolean (default true) — 정산용 신규/재등록 플래그
 * - reRegistrationMetricProfile.centerRegistrationType:
 *     'NEW' | 'RE_REGISTRATION' | 'EXCLUDED' | 'UNCLASSIFIED'
 * - reRegistrationMetricProfile.issuanceType:
 *     'PURCHASE' | 'TRANSFER' | 'CONVERSION' | 'MIGRATED'
 * - 서버 deriveCenterRegistrationType():
 *     지표 대상 아님/양도/전환 → EXCLUDED
 *     isNewSales === false     → RE_REGISTRATION
 *     이전에 다른 강사 이력    → RE_REGISTRATION
 *     그 외                    → NEW
 */

import { SessionlessInput, isSessionless } from './sessionless';
import { weeklyPace } from './pace';

/** 서버 reRegistrationMetricProfile 과 같은 값 */
export type CenterRegistrationType = 'NEW' | 'RE_REGISTRATION' | 'EXCLUDED' | 'UNCLASSIFIED';
export type IssuanceType = 'PURCHASE' | 'TRANSFER' | 'CONVERSION' | 'MIGRATED';

/** 양도·전환은 새로 끊은 수강권이 아니라 순번에서 뺀다 (서버 EXCLUDED_ISSUANCE_TYPES 와 동일) */
export const EXCLUDED_ISSUANCE_TYPES: IssuanceType[] = ['TRANSFER', 'CONVERSION'];

/** 순번을 세는 데 필요한 최소 정보 */
export type MembershipRecord = {
    membershipId: string;
    /** 개시일 (ISO). 순번 정렬 기준 */
    activationDate: string;
    /** 정렬 동률일 때 쓰는 생성 시각 (ISO) */
    createdAt?: string;
    centerRegistrationType: CenterRegistrationType;
    issuanceType: IssuanceType;
};

export type PassOrderBasis =
    /** 라포 기록만으로 확정 */
    | 'EXACT'
    /** 첫 기록이 재등록이라 이전 이력이 있다. 실제로는 더 클 수 있다 */
    | 'AT_LEAST'
    /** 아직 신규/재등록 분류가 안 된 수강권이 섞여 있다 */
    | 'UNCLASSIFIED'
    /** 셀 수 있는 수강권이 없다 (전부 양도·전환·제외) */
    | 'NONE';

export type PassOrderResult = {
    /** 계산된 순번. NONE 이면 0 */
    order: number;
    /** true 면 "N번째 이상" — 정확한 값이 아니라 하한선 */
    isAtLeast: boolean;
    basis: PassOrderBasis;
    /** 라포에 실제로 남아 있는 지표 대상 수강권 수 */
    countedRecords: number;
    /** 화면에 그대로 쓸 수 있는 라벨 */
    label: string;
    /** 왜 이 숫자가 나왔는지 — 툴팁용 */
    reason: string;
};

/** 순번 계산에 포함되는 수강권인가 */
export const isCountable = (r: MembershipRecord): boolean =>
    r.centerRegistrationType !== 'EXCLUDED' && !EXCLUDED_ISSUANCE_TYPES.includes(r.issuanceType);

const byActivation = (a: MembershipRecord, b: MembershipRecord): number => {
    const d = new Date(a.activationDate).getTime() - new Date(b.activationDate).getTime();
    if (d !== 0) return d;
    // 같은 날 개시된 건이 있으면 생성 시각으로, 그것도 같으면 id 로 순서를 고정한다.
    // 정렬이 흔들리면 새로고침할 때마다 순번이 바뀐다.
    const c = new Date(a.createdAt ?? a.activationDate).getTime() - new Date(b.createdAt ?? b.activationDate).getTime();
    if (c !== 0) return c;
    /*
     * 🔴 **`localeCompare` 를 쓰지 마세요. 코드포인트로 비교합니다.**
     *
     * ─────────────────────────────────────────────────────────────
     * 로케일마다 답이 다릅니다 — 순번이 브라우저 설정에 따라 갈립니다
     * ─────────────────────────────────────────────────────────────
     * `localeCompare` 는 실행 환경의 로케일을 따릅니다. 그런데 덴마크어(da-DK)·
     * 노르웨이어(nb-NO)에서는 **`aa` 를 `å` 로 취급**해서 알파벳 **맨 뒤**로
     * 보냅니다. 그러면 이렇게 됩니다:
     *
     *   'aa0000000000000000000000'.localeCompare('b00000000000000000000000')
     *     en-US →  -1  (aa 가 앞)
     *     da-DK →  +1  (aa 가 뒤)
     *
     * ObjectId 는 `[0-9a-f]` 라 **`aa` 로 시작하는 값이 실제로 존재**하고,
     * 개시일·생성시각이 동시에 같은 경우도 실재합니다 — 백필로 넣은 수강권들이
     * `createdAt` 을 같은 값으로 갖기 때문입니다. 즉 이 마지막 갈래는 이론이
     * 아니라 실제로 타는 길입니다.
     *
     * 여기서 순서가 갈리면 **같은 회원의 순번이 원장 브라우저마다 달라집니다** —
     * 「1번째」와 「2번째」가 갈리고, 그러면 첫 수강권 배지·이탈률·우선순위 가중이
     * 전부 함께 갈립니다. 서버 교차검증(NO_GO)에서 실측으로 잡힌 건이고,
     * 서버도 같은 이유로 코드포인트 비교로 고정했습니다(197103a1).
     *
     * 코드포인트 비교는 로케일과 무관하고 서버와 답이 같습니다.
     */
    return a.membershipId < b.membershipId ? -1 : a.membershipId > b.membershipId ? 1 : 0;
};

/**
 * 특정 수강권의 순번을 구한다.
 *
 * @param records 이 회원의 전체 수강권 (센터 단위)
 * @param targetId 순번을 알고 싶은 수강권. 생략하면 가장 최근 것
 */
export const resolvePassOrder = (
    records: MembershipRecord[],
    targetId?: string
): PassOrderResult => {
    const countable = records.filter(isCountable).sort(byActivation);

    if (countable.length === 0) {
        return {
            order: 0,
            isAtLeast: false,
            basis: 'NONE',
            countedRecords: 0,
            label: '순번 없음',
            reason: '양도·전환으로만 받은 수강권이라 순번을 세지 않습니다.',
        };
    }

    const target = targetId
        ? countable.find((r) => r.membershipId === targetId)
        : countable[countable.length - 1];

    if (!target) {
        return {
            order: 0,
            isAtLeast: false,
            basis: 'NONE',
            countedRecords: countable.length,
            label: '순번 없음',
            reason: '이 수강권은 순번 계산 대상이 아닙니다 (양도·전환 또는 지표 제외).',
        };
    }

    const recordIndex = countable.indexOf(target) + 1;
    const earliest = countable[0];

    // 가장 이른 수강권이 재등록이면 라포 이전에 다닌 이력이 있다는 뜻이다.
    // 몇 번인지는 모르므로 최소 한 번으로 보고 +1 만 한다.
    const hasHiddenHistory = earliest.centerRegistrationType === 'RE_REGISTRATION';
    const unclassified = countable.some((r) => r.centerRegistrationType === 'UNCLASSIFIED');

    const order = recordIndex + (hasHiddenHistory ? 1 : 0);

    if (unclassified) {
        return {
            order,
            isAtLeast: hasHiddenHistory,
            basis: 'UNCLASSIFIED',
            countedRecords: countable.length,
            label: `${order}번째 (확인 필요)`,
            reason:
                '신규/재등록 분류가 안 된 수강권이 있어 순번이 달라질 수 있습니다. 수강권 등록 화면에서 분류를 지정해 주세요.',
        };
    }

    if (hasHiddenHistory) {
        return {
            order,
            isAtLeast: true,
            basis: 'AT_LEAST',
            countedRecords: countable.length,
            label: `${order}번째 이상`,
            reason:
                `라포에 기록된 수강권은 ${countable.length}건이지만, 첫 수강권이 재등록으로 등록돼 있습니다. ` +
                '라포 도입 전부터 다니던 회원이라 실제 순번은 이보다 클 수 있습니다.',
        };
    }

    return {
        order,
        isAtLeast: false,
        basis: 'EXACT',
        countedRecords: countable.length,
        label: `${order}번째`,
        reason: `라포에 기록된 수강권 ${countable.length}건을 개시일 순으로 센 값입니다.`,
    };
};

/**
 * 순번별 이탈률 — 2024-08 라이브 백업(수강권 1.5만 건) 실측.
 *
 * 방법론: rappo-server/scripts/prod_readonly_pass_order_churn.js
 *   · 코호트: 정상 종료(만료·소진)된 수강권 2,194건
 *   · 이탈: 종료 후 90일 동안 출석·노쇼 기록이 한 건도 없음
 *   · 순번: 이 파일의 규칙과 동일 (TRANSFER/CONVERSION 제외, 개시일 순)
 *   · 95% 신뢰구간: 1번째 ±2.4pp (n=1,689) / 2번째 ±5.1pp (n=363)
 *     / 3번째 ±9.2pp (n=104)
 * 4번째 이상은 표본 38명이라 값을 만들지 않는다.
 *
 * ⚠️ 이전 값 68.3 / 48.2 / 34.4 는 레포 어디에서도 재현되지 않아 폐기했다.
 *    문서가 출처로 대던 스크립트(prod_readonly_metrics_4.js)에는 순번별
 *    집계가 없다. 되돌리려면 재현 가능한 스크립트부터 만들어야 한다.
 *
 * ⚠️ 1번째와 2번째가 사실상 같다(47.1 vs 46.6, 신뢰구간이 겹침).
 *    "첫 수강권이 유일하게 위험한 구간"이라는 서사는 이 데이터가
 *    지지하지 않는다 — 첫 두 수강권이 똑같이 위험하고, 3번째부터
 *    떨어진다. 문구를 쓸 때 두 값의 0.5pp 차이에 의미를 부여하지 말 것.
 *
 * 운영 DB 로 재계산하려면 (VPC 안에서):
 *   MONGODB_URL='...' CHURN_DAYS=90 node scripts/prod_readonly_pass_order_churn.js
 */
export const CHURN_RATE_BY_ORDER: Record<number, number> = {
    1: 47.1,
    2: 46.6,
    3: 36.5,
};

/**
 * 첫 수강권 구간의 이탈률(%).
 *
 * ⚠️ 화면 문구에 숫자를 직접 적지 마세요. 이 값을 읽어서 렌더하세요.
 * "이탈률 68.3% 구간" 같은 문자열이 여러 파일에 박혀 있었는데,
 * 이 수치는 라이브 데이터로 주기적으로 재계산됩니다. 한 곳만 고치면
 * 나머지가 옛 숫자를 계속 보여주고, 원장은 같은 화면에서 서로 다른
 * 이탈률을 읽게 됩니다. 원천은 CHURN_RATE_BY_ORDER 하나입니다.
 */
export const FIRST_PASS_CHURN_RATE = CHURN_RATE_BY_ORDER[1];

export type ChurnRateLookup = {
    /** 이탈률(%). 검증 구간 밖이면 null */
    rate: number | null;
    /** 화면 문구 */
    text: string;
};

export const lookupChurnRate = (result: PassOrderResult): ChurnRateLookup => {
    if (result.basis === 'NONE') {
        return { rate: null, text: '순번을 셀 수 없어 이탈률을 적용하지 않습니다' };
    }
    const rate = CHURN_RATE_BY_ORDER[result.order];
    if (rate == null) {
        return {
            rate: null,
            text: `${result.order}번째 이상 구간은 아직 검증된 이탈률이 없습니다`,
        };
    }
    if (result.isAtLeast) {
        // 순번이 하한선이므로 이탈률은 이 값보다 낮을 가능성이 크다.
        // 위험을 과대평가하지 않도록 방향을 명시한다.
        return {
            rate,
            text: `${result.order}번째 기준 ${rate}% · 실제로는 이보다 낮을 수 있습니다`,
        };
    }
    return { rate, text: `${result.order}번째 기준 이탈률 ${rate}%` };
};

/**
 * 첫 수강권(=이탈률 68.3% 집중 관리 구간)인가.
 *
 * 확정된 경우에만 true 를 준다.
 *   - 재등록으로 들어온 회원(AT_LEAST)은 순번이 2 이상이라 제외된다.
 *   - 분류가 안 된 수강권(UNCLASSIFIED)도 제외한다. 나중에 재등록으로 밝혀지면
 *     실제로는 2번째인데 68.3% 위험군에 넣어버리게 된다. 모를 때는 단정하지 않는다.
 *
 * 이 함수 하나로 판정해야 화면마다 기준이 갈리지 않는다.
 */
export const isFirstPass = (result: PassOrderResult): boolean =>
    result.basis === 'EXACT' && result.order === 1;

/**
 * 원장이 손봐야 분류가 끝나는 회원인가.
 * 이런 회원은 순번을 믿을 수 없으므로 화면에서 따로 모아 알려준다.
 */
export const needsClassification = (result: PassOrderResult): boolean =>
    result.basis === 'UNCLASSIFIED';

/* ───────────────────────── 회원 단계 ───────────────────────── */

/**
 * 회원 단계 판정.
 *
 * 순번과 단계를 각각 손으로 적어두면 반드시 어긋난다.
 * 재등록으로 들어온 회원의 순번을 2로 고쳐도 단계가 '첫 수강권'으로 남아 있으면
 * 화면에는 여전히 68.3% 구간으로 보인다. 그래서 둘 다 여기서 계산한다.
 *
 * 우선순위가 중요하다.
 *   ① 이탈 위험  — 이미 안 나오는 회원. 재등록 이야기를 꺼낼 단계가 아니다.
 *   ② 골든타임   — 아직 다니지만 수강권이 끝나간다.
 *   ③ 첫 수강권  — 확정된 1번째만. 재등록·미분류는 여기 들어오지 않는다.
 *   ④ 안정 궤도  — **적극 판정.** meetsStable 을 전부 통과해야 얻는다.
 *   ⑤ 주의       — 위험까진 아니지만 안정이라 부를 수 없는 회원. 나머지는 여기로.
 */
export type MemberStageKey = 'FIRST_PASS' | 'STABLE' | 'WATCH' | 'GOLDEN' | 'AT_RISK';

export type StageInput = SessionlessInput & {
    /**
     * 소진 페이스 변화율(%).
     *
     * null 은 "안 줄었다"가 아니라 **"비교할 기록이 없다"** 입니다.
     * 두 달 전에 등록한 회원은 이전 60일 수업이 0회라 나눌 수가 없습니다.
     * 이걸 0으로 채우면 괜찮아 보이고, -100으로 채우면 신규 회원 전원이
     * 이탈 위험으로 뜹니다. 둘 다 틀립니다.
     */
    paceDeltaPct: number | null;
    /** 마지막 수업이 며칠 전인가. 수업 기록이 아예 없으면 null */
    lastLessonDaysAgo: number | null;
    /**
     * 최근 60일 노쇼율(%). 수업이 없으면 null.
     *
     * ⚠️ **단계 판정에 쓰지 않습니다.** 이탈 예측력이 약했고(1.75배), 무엇보다
     *    대표 지시로 **회원 현황 화면의 표시에서 노쇼율을 뺐습니다**
     *    (자리를 당일 취소·변경으로 교체 — tabData.ts 주석).
     *    화면에 안 보이는 값으로 회원을 「주의」에 내려보내면 원장님이
     *    "왜 여기 있나"를 읽을 수 없고, 그러면 그 명단은 강사에게 전달되지 않습니다.
     *    서버는 이 값을 계속 보내므로, 분포를 실측한 뒤 다시 검토할 수 있습니다.
     */
    noshowPct: number | null;
    /** 이전·최근 구간을 합친 수업 수. "한 번이라도 왔는가"를 판단한다 */
    lessonCount?: number;
    /**
     * 최근 60일 수업 수 (서버 `paceNow`). **주당 횟수가 아니라 총합**입니다.
     * 주당 환산은 `pace.weeklyPace()` 한 곳에서만 합니다.
     *
     * 변화율(`paceDeltaPct`)이 못 보는 **절대량**을 여기서 봅니다.
     * 원래 주 0.3회로 다니던 회원은 변화가 없어서 변화율로는 영원히 안 걸립니다.
     *
     * ⚠️ 이 값을 **고정 문턱과 비교하지 않습니다.** 센터 중앙값과 비교합니다
     *    (`meetsFrequency`). 센터 기준선이 없으면 그 비교 자체를 하지 않으므로,
     *    지금은 이 값을 안 넘겨도 아무도 떨어지지 않습니다.
     *    다만 기준선이 들어온 뒤에는 안 넘긴 값이 0회로 읽히므로,
     *    호출부는 서버가 준 `paceNow` 를 그대로 넘겨야 합니다
     *    (실서버 경로 `adapters.toMemberView` 는 `...dto` 로 항상 넘깁니다).
     */
    paceNow?: number;
    /**
     * 최근 60일 당일 취소·변경 횟수 (서버 `sameDayAdjustments`).
     *
     * ⚠️ `null`(못 쟀다)·키 없음(구버전 서버)은 **경고로 읽지 않습니다.**
     *    모르는 것은 "괜찮다"도 "잦다"도 아닙니다 (memberSignals 와 같은 규칙).
     */
    sameDayAdjustments?: number | null;
    /**
     * 그 센터 활성 회원의 주당 수업 횟수 **중앙값** (최근 60일 기준).
     *
     * ⚠️ **평균이 아니라 중앙값입니다.** 주 5회 다니는 회원 몇 명이 평균을 끌어올립니다.
     * 서버가 안 주면 `null`(또는 키 없음)이고, 그때는 빈도로 판정하지 않습니다
     * (`meetsFrequency` 의 (c)).
     *
     * 서버 신규 필드 S1 — 2026-08-27 대표 결정. 아직 배포 전입니다.
     */
    centerLessonsPerWeekMedian?: number | null;
    /** 위 중앙값을 낸 회원 수. 표본이 얇으면 믿지 않습니다 (`MIN_CENTER_SAMPLE`) */
    centerLessonsSampleSize?: number | null;
    /** 잔여 회차. 회차 정보가 온전하지 않으면 null */
    remain: number | null;
    /** 만료까지 남은 일수. 만료일이 없으면 null */
    expiryDday: number | null;
};

/**
 * 이탈이 이미 시작됐다고 볼 기준.
 *
 * ⚠️ 서버 opsPace.service.ts 의 AT_RISK_RULE 과 값이 같아야 합니다.
 * 다르면 오늘의 브리핑에는 안 뜬 회원이 회원 현황에서는 위험으로 뜹니다.
 */
export const AT_RISK_RULE = {
    paceDropPct: -70,
    lastLessonDays: 12,
};

/**
 * 이만큼 넘게 비면 서버는 **위험이 아니라 「휴면」**으로 부른다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 이 값은 프론트 판정을 **바꾸지 않습니다.** 표시만 바꿉니다
 * ─────────────────────────────────────────────────────────────
 * 여기가 이 저장소의 **정본**입니다 (서버 `opsPace.service.ts` 의 같은 이름 상수와
 * 값이 같아야 합니다 — 다르면 두 화면이 같은 회원을 두고 다른 이름으로 부릅니다).
 *
 * 서버는 공백이 이 값을 넘으면 브리핑 항목을 `DORMANT` 로 만듭니다
 * (`dormant-{memberId}` · grade `WARNING`). 운영 실측에서 위험 27건의 공백
 * 중앙값이 62일이었고, 우선순위가 공백에 비례해 **매일 같은 이름이 1순위를
 * 점거**했기 때문입니다.
 *
 * ⚠️ **`resolveStage` 는 이 값을 보지 않습니다. 일부러 그렇습니다.**
 *    회원 현황은 전수 명단이라, 62일 안 나온 회원이 「이탈 위험」 칸에 그대로
 *    서 있는 것이 목적에 맞습니다 — 칸을 옮기면 원장님이 전수 명단에서
 *    그 회원을 찾을 곳이 사라집니다. 대신 그 칸에 **「휴면」 보조 배지**를 달아
 *    두 화면의 차이를 화면이 스스로 설명하게 합니다
 *    (`MemberSummaryRow` · 2026-08-27 결정 ③).
 *
 * ⚠️ 출석 기록이 아예 없는 회원(`lastLessonDaysAgo == null`)은 **휴면이 아닙니다.**
 *    등록만 하고 한 번도 안 온 것은 이탈이 아니라 온보딩 문제라 대응이 다릅니다.
 *    null 을 "공백 무한대"로 읽으면 그 집단이 통째로 휴면이 됩니다 (서버 judge 와 같은 규칙).
 */
export const DORMANT_GAP_DAYS = 45;

/**
 * 서버가 「휴면」이라고 부를 공백인가 — 서버 `judge()` 의 `dormantGap` 과 같은 식.
 *
 * 판정에 쓰지 않습니다. **배지에만** 씁니다 (위 상수 주석).
 */
export const isDormantGap = (input: Pick<StageInput, 'lastLessonDaysAgo'>): boolean =>
    input.lastLessonDaysAgo != null && input.lastLessonDaysAgo > DORMANT_GAP_DAYS;

/*
 * 🔴 여기 있던 `noshowPct: 2.5` 를 뺐습니다 (2026-08-27).
 *
 * 기록된 대표 결정이 「노쇼율 — 단독 판정 금지 · **표시만**」(GAP_REPORT §3-2)이라
 * 이 값은 판정에 쓰이지 않았고, 화면 어느 곳도 읽지 않았습니다. 그런데도 판정
 * 기준 객체 안에 앉아 있어서, 다음 사람이 "여기 있으니 쓰는 값"으로 읽고
 * 되살릴 여지가 남아 있었습니다. 상수를 지우면 되살리려면 결정을 먼저 뒤집어야 합니다.
 *
 * ⚠️ **화면 표시는 그대로입니다.** 브리핑 카드의 근거 수치와 근거 모달은 노쇼율을
 *    계속 보여주고, 서버도 `noshowPct` 를 계속 보냅니다. 뺀 것은 판정뿐입니다.
 *    (회원 현황 칸만 대표 지시로 당일 취소·변경이 자리를 넘겨받았습니다)
 */

/** 재등록 상담을 꺼내야 하는 기준 */
export const GOLDEN_RULE = {
    remain: 3,
    expiryDday: 14,
};

/**
 * 안정 궤도로 인정하는 기준 — **전부 만족해야 안정입니다.**
 *
 * ─────────────────────────────────────────────────────────────
 * 안정은 판정이 아니라 fallthrough 였습니다
 * ─────────────────────────────────────────────────────────────
 * 예전에는 "이탈 위험도 골든타임도 첫 수강권도 아니면 안정 궤도"였습니다.
 * 그래서 **두 달 넘게 안 나온 회원이 안정 궤도에 앉아 있었습니다.**
 * 안정 궤도 카드에는 "여기는 손대지 않는 게 맞습니다"라고 적혀 있으니,
 * 원장님은 그 회원을 안 챙겨도 되는 사람으로 읽습니다.
 *
 * 공백 조건을 달아 한 번 좁혔지만, 문턱 **바로 아래** 회원들은 여전히
 * 갈 곳이 없었습니다. 그 사람들을 '이탈 위험'으로 보내면 멀쩡한 회원이
 * 위험 명단에 섞이고, '안정'에 두면 안 챙겨도 되는 사람이 됩니다.
 * 둘 다 틀립니다 — 그래서 **「주의」 단계**를 새로 두고,
 * 안정은 **통과해야 얻는** 단계로 바꿉니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 네 축은 전부 화면에 이미 보이는 신호입니다
 * ─────────────────────────────────────────────────────────────
 * 이유를 못 대는 명단은 원장님이 강사에게 전달할 수 없습니다.
 * 그래서 **화면에 대응 배지가 없는 축은 쓰지 않습니다.**
 * (노쇼율이 그래서 빠졌습니다 — StageInput.noshowPct 주석 참고)
 */
export const STABLE_RULE = {
    /**
     * 마지막 수업이 이 일수 안이면 다니고 있는 것으로 본다.
     *
     * ⚠️ 새로 정한 숫자가 아닙니다. 화면이 이미 '2주 넘게 공백'으로 부르던
     *    기준(memberSignals.gapAlertDays 의 안정 구간 값)입니다.
     *    한쪽만 고치면 목록에 '14일 공백' 배지가 붙은 회원이
     *    단계는 '안정 궤도'인 화면이 됩니다.
     */
    maxGapDays: 14,

    /**
     * 페이스가 이만큼 넘게 꺾이면 안정이 아니다 (이탈 위험 문턱 −70% 보다 느슨하다).
     *
     * ⚠️ 새로 정한 숫자가 아닙니다. `memberSignals.isPaceAlert` 가 이미
     *    이 값으로 목록에 붉은 경고 배지를 붙이고 있습니다.
     *    여기서 빼면 **배지가 붙은 회원의 단계가 '안정 궤도'** 인 화면이 됩니다.
     */
    paceDropPct: -30,

    /**
     * 당일 취소·변경이 이 횟수 이상이면 안정이 아니다.
     *
     * ⚠️ 새로 정한 숫자가 아닙니다. 서버 `opsMemberData.SAME_DAY_ADJUST_ALERT`
     *    와 같은 값이고, `memberSignals.isSameDayAdjustAlert` 가 이미 이 값으로
     *    배지를 붙입니다. 회원 현황에서 노쇼율 자리를 넘겨받은 지표입니다.
     */
    sameDayAdjustments: 3,
};

/*
 * 🔴 여기 있던 `lessonsPerWeek` 를 뺐습니다 (2026-08-27 대표 결정).
 *
 * 「주 0.75회」로 들어왔던 값을 실측 근거(「주당 0.5회 이하 8명」)에 맞춰 0.5 로
 * 내려 두고 있었습니다. 그런데 대표 결정은 **숫자를 고르는 문제가 아니라고**
 * 답했습니다 — 고정값 자체를 버립니다.
 *
 *   "이전 60일과 최신 60일을 비교해 페이스가 떨어지지 않고 동일하게 계속
 *    나온다면 챙겨야 할 회원이 아니다. 절대 횟수 기준이 필요하다면
 *    **그 센터 회원들의 기준**에 맞춰서 정한다."
 *
 * 한 달에 두 번씩 **꾸준히** 오시는 분은 빠지는 게 아니라 원래 그 페이스입니다.
 * 그분에게 강사가 전화하면 「내가 뭘 잘못했나」가 됩니다.
 * 그리고 「자주 온다」의 기준은 센터마다 다릅니다 — 주 3회가 보통인 센터와
 * 주 1회가 보통인 센터에 같은 선을 그으면 한쪽은 전원이 걸리고 다른 쪽은
 * 아무도 안 걸립니다.
 *
 * → `meetsFrequency()` 로 옮겼습니다. 페이스 유지 면제 + 센터 상대 기준.
 */

/**
 * 센터 기준선에 곱하는 계수. 강사 정착의 `coachSettling.RELATIVE_FLOOR` 과 같은
 * 취지입니다 — 「센터 보통보다 **확실히** 처진 경우」만 잡습니다.
 *
 * ⚠️ 잠정값입니다. 서버 3차에서 회원 수업 빈도 분포를 재고 확정합니다.
 */
export const MEMBER_RELATIVE_FLOOR = 0.7;

/**
 * 이 인원 아래에서는 센터 중앙값을 믿지 않습니다.
 * 회원 몇 명으로 중앙값을 내면 숫자 장난이 됩니다
 * (강사 쪽 `coachSettling.MIN_JUDGE_DENOM` 과 같은 취지).
 */
export const MIN_CENTER_SAMPLE = 10;

/**
 * 「자주 오는가」 — 2026-08-27 대표 결정으로 규칙이 바뀌었습니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 지금은 이 축이 **아무도 떨어뜨리지 않습니다. 의도된 상태입니다.**
 * ─────────────────────────────────────────────────────────────
 * 센터 기준선(S1: `centerLessonsPerWeekMedian`)을 서버가 아직 안 줍니다.
 * 그래서 (c)에 걸려 전원 통과합니다. 고정값 0.5/0.75 를 그대로 두는 것보다
 * **안 잡는 편이 낫다**는 것이 대표 결정입니다 — 근거 없이 명단에 올린
 * 회원에게 강사가 전화하면 그 명단 전체를 못 믿게 되기 때문입니다.
 *
 * 기준선이 들어오면 (b)가 저절로 살아납니다. 화면 코드는 그대로입니다.
 *
 * ⚠️ **판정과 설명이 같은 함수를 봅니다.** `meetsStable` 의 ②축과
 *    `watchReasons` 의 `RARE` 가 **둘 다 여기를** 부릅니다. 한쪽만 고치면
 *    「주의」 칸에 이유 없이 서 있는 회원이 생깁니다.
 */
export type FrequencyInput = Pick<
    StageInput,
    'paceDeltaPct' | 'paceNow' | 'centerLessonsPerWeekMedian' | 'centerLessonsSampleSize'
>;

export const meetsFrequency = (input: FrequencyInput): boolean => {
    /*
     * (a) 페이스가 유지되고 있으면 **빈도를 아예 보지 않습니다.**
     *
     * 「떨어지지 않았다」의 기준은 이 함수 밖에서 이미 쓰는 선(−30%)과 같습니다.
     * 여기만 다른 선을 쓰면 한 판정 안에 「떨어졌다」가 두 개가 됩니다.
     * (null 은 '유지됐다'가 아니라 '비교할 기록이 없다' — 면제하지 않고 (b)(c)로 갑니다)
     */
    if (input.paceDeltaPct != null && input.paceDeltaPct > STABLE_RULE.paceDropPct) return true;

    /*
     * (c) 센터 기준선을 못 내면 **판정하지 않습니다.**
     * 표본이 얇은 센터에서 중앙값을 내면 숫자 장난이 됩니다.
     */
    const median = input.centerLessonsPerWeekMedian;
    const sample = input.centerLessonsSampleSize ?? 0;
    if (median == null || median <= 0 || sample < MIN_CENTER_SAMPLE) return true;

    /*
     * (b) 센터 보통보다 확실히 처졌는가.
     *
     * ⚠️ 주당 환산은 `pace.weeklyPace()` 를 씁니다 — **화면에 뜨는 그 값**입니다.
     *    여기서 `paceNow / 8.57` 을 직접 적으면 화면에 「주 0.5회」로 적힌 회원이
     *    원값 0.467 로 판정돼 주의에 서게 됩니다. 원장님이 읽기에 그건 고장입니다.
     *    반올림은 pace.ts 한 곳에서만 하고, 판정도 그 값을 봅니다.
     */
    return weeklyPace(input.paceNow ?? 0) >= median * MEMBER_RELATIVE_FLOOR;
};

/**
 * 안정 궤도라고 부를 수 있는가 — 네 축을 **전부** 통과해야 한다.
 *
 * 못 넘으면 '이탈 위험'이 아니라 '주의'로 갑니다. 둘은 다른 말입니다 —
 * 위험은 "이미 떠나는 중", 주의는 "아직 괜찮지만 안심할 단계는 아니다"입니다.
 *
 * ⚠️ **모르는 값으로 떨어뜨리지 않습니다.** 센터 기준선이 없거나
 *    `sameDayAdjustments` 가 null 이면 그 축은 건너뜁니다.
 *    모르는 것을 "나쁘다"로 읽으면 구버전 서버를 쓰는 센터의 회원이
 *    전부 주의로 내려갑니다.
 */
export const meetsStable = (input: StageInput): boolean => {
    /*
     * ① 최근에 왔는가.
     *
     * 공백이 길어도 **주당 수업이 이전보다 늘어난** 회원은 통과시킵니다.
     * 늘려서 다니는 사람을 명단에 올리면 명단 전체를 못 믿게 됩니다.
     * (null 은 '안 늘었다'가 아니라 '비교할 기록이 없다' — 늘었다고 말하지 않는다)
     */
    const activeRecently =
        input.lastLessonDaysAgo != null && input.lastLessonDaysAgo < STABLE_RULE.maxGapDays;
    const pacePickedUp = input.paceDeltaPct != null && input.paceDeltaPct > 0;
    if (!activeRecently && !pacePickedUp) return false;

    /* ② 얼마나 자주 오는가 — 고정 문턱이 아니라 **센터 기준**이다 (meetsFrequency) */
    if (!meetsFrequency(input)) return false;

    /* ③ 페이스가 크게 꺾이지 않았는가. null 은 '비교할 기록이 없다' — 신규를 떨어뜨리지 않는다 */
    if (input.paceDeltaPct != null && input.paceDeltaPct <= STABLE_RULE.paceDropPct) return false;

    /* ④ 약속을 지키는가. null·키 없음은 경고로 읽지 않는다 */
    if (
        input.sameDayAdjustments != null &&
        input.sameDayAdjustments >= STABLE_RULE.sameDayAdjustments
    ) {
        return false;
    }

    return true;
};

/**
 * 「주의」에 걸린 **이유**. 화면이 "왜 여기 있나"를 설명하는 데 씁니다.
 *
 * 🔴 근거를 못 대면 원장님이 강사에게 전달할 수 없습니다.
 *    이유가 비면 그 회원은 명단에서 설명 없이 서 있게 되고,
 *    한 번 그러면 다음부터 그 칸을 안 봅니다.
 *
 * 네 축이 그대로 네 가지 이유가 됩니다 — 판정과 설명이 갈릴 수 없습니다.
 */
export type WatchReason = 'GAP' | 'RARE' | 'SLOWING' | 'UNRELIABLE';

export const WATCH_REASON_TEXT: Record<WatchReason, string> = {
    GAP: `${STABLE_RULE.maxGapDays}일 넘게 수업 공백`,
    /*
     * 🔴 여기에 숫자를 적지 마세요. 선이 **센터마다 다릅니다** (meetsFrequency).
     *    「주당 수업 0.5회 미만」이라고 적으면, 센터 중앙값이 주 3회인 곳에서
     *    주 1.5회로 걸린 회원 옆에 "0.5회 미만"이라는 거짓 근거가 붙습니다.
     */
    RARE: '센터 보통보다 수업이 적음',
    SLOWING: `소진 페이스 ${Math.abs(STABLE_RULE.paceDropPct)}% 이상 감소`,
    UNRELIABLE: `당일 취소·변경 ${STABLE_RULE.sameDayAdjustments}회 이상`,
};

export const watchReasons = (input: StageInput): WatchReason[] => {
    const out: WatchReason[] = [];
    const activeRecently =
        input.lastLessonDaysAgo != null && input.lastLessonDaysAgo < STABLE_RULE.maxGapDays;
    const pacePickedUp = input.paceDeltaPct != null && input.paceDeltaPct > 0;
    if (!activeRecently && !pacePickedUp) out.push('GAP');
    /* 판정(meetsStable ②)과 **같은 함수**를 부른다 — 조건을 여기 다시 적지 않는다 */
    if (!meetsFrequency(input)) out.push('RARE');
    if (input.paceDeltaPct != null && input.paceDeltaPct <= STABLE_RULE.paceDropPct) {
        out.push('SLOWING');
    }
    if (
        input.sameDayAdjustments != null &&
        input.sameDayAdjustments >= STABLE_RULE.sameDayAdjustments
    ) {
        out.push('UNRELIABLE');
    }
    return out;
};

/**
 * 회원이 지금 어느 단계인가.
 *
 * ─────────────────────────────────────────────────────────────
 * 이탈 위험은 두 조건이 **함께** 맞아야 합니다
 * ─────────────────────────────────────────────────────────────
 * 페이스만 꺾인 회원(주 3회 → 주 1회인데 어제 왔다)은 위험이 아닙니다.
 * 공백만 긴 회원(원래 한 달에 한 번 오는 분)도 위험이 아닙니다.
 * 둘 다일 때만 "오던 사람이 안 온다"가 됩니다.
 *
 * 예전에는 셋 중 하나만 맞아도 위험으로 봤는데, 그러면 원장님이
 * 멀쩡한 회원 명단을 받게 됩니다. 한 번 그러면 다음부터 안 봅니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 `memberOrder` 는 **회원의 최신 수강권 순번**입니다 — 카드 것이 아닙니다
 * ─────────────────────────────────────────────────────────────
 * 이 함수가 순번을 쓰는 곳은 **딱 한 줄**, 첫 수강권 게이트뿐입니다.
 * 그리고 그 게이트가 묻는 것은 **「이 회원이 지금 첫 수강권 국면인가」** 이지
 * 「이 카드가 1번째 수강권인가」가 아닙니다.
 *
 *   · **첫 수강권 = 회원의 국면** — 아직 한 번도 재등록한 적 없는 사람.
 *     이탈률이 가장 높은 구간이고, 그래서 이 칸이 존재합니다.
 *   · **순번 = 카드의 사실** — 이 수강권이 그 회원의 몇 번째인가.
 *     라벨·요율·이탈률 표시가 쓰는 값이고, 카드마다 다릅니다.
 *
 * 둘을 같은 값으로 두면 한쪽이 반드시 틀립니다. 수강권을 2건 가진 회원의
 * **옛 1번째 카드**를 생각해 보세요 — 그 카드의 순번은 정말 1번째이지만,
 * 그 회원은 **이미 재등록했습니다.** 카드 순번으로 게이트를 열면 이미 다음
 * 수강권을 산 사람이 「첫 수강권 · 이탈률 가장 높은 구간」 칸에 섭니다.
 * (2026-08-31 실측: 그렇게 하면 첫 수강권이 47 → 71장으로 51% 부풉니다)
 *
 * 그래서 **부르는 쪽이 회원 단위 순번을 넘깁니다** — `resolvePassOrder(records)`
 * 를 targetId 없이 부른 값입니다(기본 동작이 「가장 최근 수강권」입니다).
 * 카드 순번은 `MemberView.passOrder` 에 따로 들어 있습니다(`adapters.toMemberView`).
 *
 * ⚠️ 여기에 카드 순번을 넘기지 마세요. 컴파일은 되고 화면도 그럴듯하게 뜹니다 —
 *    틀린 건 명단의 **의미**라서 숫자만 봐서는 안 보입니다.
 */
export const resolveStage = (input: StageInput, memberOrder: PassOrderResult): MemberStageKey => {
    const dropped = input.paceDeltaPct != null && input.paceDeltaPct <= AT_RISK_RULE.paceDropPct;
    const absent =
        input.lastLessonDaysAgo != null && input.lastLessonDaysAgo >= AT_RISK_RULE.lastLessonDays;

    if (dropped && absent) return 'AT_RISK';

    /*
     * 비교 기준이 없는(신규) 회원이라도 공백이 길면 챙깁니다.
     * 단 수업 기록이 아예 없으면 이탈이 아니라 온보딩 문제라 여기서 다루지 않습니다.
     */
    if (input.paceDeltaPct == null && absent && (input.lessonCount ?? 0) > 0) {
        return 'AT_RISK';
    }

    /*
     * 회차 개념이 없는 수강권(무제한·대관)은 **잔여 회차로 골든타임을 판정하지 않습니다.**
     *
     * 등록상 999회라 지금은 어차피 걸리지 않지만, 그건 우연이지 규칙이 아닙니다.
     * 명시적으로 빼 두지 않으면 "잔여 999회가 3회에 가까워지면 상담하세요" 같은
     * 회차 기반 안내가 다시 붙습니다 (sessionless.ts 주석의 실제 화면).
     *
     * ⚠️ 만료일 기준(nearExpiry)은 **그대로 둡니다.** 대관에도 만료일은 있고,
     *    그건 진짜로 상담을 꺼내야 하는 시점입니다.
     */
    const lowRemain =
        !isSessionless(input) && input.remain != null && input.remain <= GOLDEN_RULE.remain;
    // 이미 만료된 수강권(D-day 음수)은 오늘 챙길 일이 아니다
    const nearExpiry =
        input.expiryDday != null &&
        input.expiryDday >= 0 &&
        input.expiryDday <= GOLDEN_RULE.expiryDday;
    if (lowRemain || nearExpiry) return 'GOLDEN';

    /*
     * 첫 수강권 국면인가 — **회원 기준**이다 (위 함수 주석의 `memberOrder` 설명).
     * 이미 다음 수강권을 산 회원은 옛 1번째 카드를 들고 있어도 여기 안 선다.
     */
    if (isFirstPass(memberOrder)) return 'FIRST_PASS';

    /*
     * ── 안정 궤도인가 ────────────────────────────────────────
     *
     * 여기까지 온 회원은 "2번째 이상 수강권"입니다. 예전에는 그것만으로
     * 안정 궤도였고, 그다음에는 공백 하나만 더 봤습니다.
     * 이제 **네 축을 전부 통과해야** 안정입니다 (meetsStable).
     */
    if (meetsStable(input)) return 'STABLE';

    /*
     * ── 주의 ────────────────────────────────────────────────
     *
     * 안정 문턱을 못 넘은 회원은 전부 여기로 옵니다.
     *
     * 🔴 예전에는 이 자리에서 **'이탈 위험'으로 보냈습니다.** 그건 서버
     *    AT_RISK_RULE 보다 넓어서, 오늘의 브리핑에는 안 뜨는데 회원 현황에서만
     *    위험으로 뜨는 회원을 만들었습니다(옛 주석이 그걸 자인하고 있었습니다).
     *    이제 그 회원들은 '주의'에 섭니다 — 위험 명단이 서버와 다시 같아지고,
     *    "아직 괜찮지만 안심할 단계는 아니다"라는 사실도 안 사라집니다.
     *
     * 수업 기록이 아예 없는 회원(lastLessonDaysAgo == null)도 여기로 옵니다.
     * 예전에는 '안정 궤도'에 뒀는데, 한 번도 안 온 사람에게
     * "여기는 손대지 않는 게 맞습니다"라고 적는 것이 더 나빴습니다.
     * '이탈 위험'이라고 부를 근거도 없으므로(등록만 하고 안 온 것은 이탈이 아니라
     * 온보딩 문제입니다) 주의가 맞는 자리입니다.
     */
    return 'WATCH';
};
